import { SurveyV2 } from '@act/shared/data-transfer-objects';
import {
  Box,
  FlexContainer,
  IconLibrary,
  IconName,
  TaggableChip,
} from '@act/storybook';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/userMonitoring.util';
import { Typography } from '@mui/material';
import { ReactNode } from 'react';
import {
  formatHistoryTimestamp,
  formatResponseAnswer,
  getResponseStateChips,
  groupHistoryResponsesByPhase,
} from '../assignedSurveyHistory.util';

interface AssignedSurveyHistoryTimelineProps {
  history: SurveyV2.AssignedSurveyHistoryDto;
}

/** Answer text is member PHI, so it is masked in session replay. */
const maskedAnswerProps = generateDatadogTrackingProps(null, {
  privacyLevel: 'mask',
});

const SectionHeading = ({
  iconName,
  title,
}: {
  iconName: IconName;
  title: string;
}) => (
  <FlexContainer direction="row" alignItems="center" gap={1} height="auto">
    <IconLibrary iconName={iconName} size={18} />
    <Typography component="div" variant="titleMedium">
      {title}
    </Typography>
  </FlexContainer>
);

const LabeledValue = ({ label, value }: { label: string; value: string }) => (
  <Box display="flex" flexDirection="column">
    <Typography component="div" variant="labelMedium" color="text.secondary">
      {label}
    </Typography>
    <Typography component="div" variant="bodyMedium">
      {value}
    </Typography>
  </Box>
);

const HistoryCard = ({ children }: { children: ReactNode }) => (
  <Box
    display="flex"
    flexDirection="column"
    gap="8px"
    padding="12px 16px"
    backgroundColor="surfaceContainer.main"
    borderRadius="8px"
  >
    {children}
  </Box>
);

const ResponseRow = ({
  response,
}: {
  response: SurveyV2.AssignedSurveyHistoryResponseDto;
}) => {
  const chips = getResponseStateChips(response);

  return (
    <Box display="flex" flexDirection="column" gap="2px">
      <FlexContainer
        direction="row"
        alignItems="center"
        gap={1}
        flexWrap
        height="auto"
      >
        <Typography component="div" variant="subheadLarge" fontWeight={600}>
          {response.questionText || response.questionTitle}
        </Typography>
        {chips.map(chip => (
          <TaggableChip
            key={chip.label}
            label={chip.label}
            color={chip.color}
            size="small"
            isTag
          />
        ))}
      </FlexContainer>
      <Typography component="div" variant="bodyMedium" {...maskedAnswerProps}>
        {formatResponseAnswer(response)}
      </Typography>
      <Typography component="div" variant="labelMedium" color="text.secondary">
        {`Answered ${formatHistoryTimestamp(response.createdAt)}`}
        {response.jumpedAt
          ? ` · jumped ${formatHistoryTimestamp(response.jumpedAt)}`
          : ''}
        {response.deletedAt
          ? ` · deleted ${formatHistoryTimestamp(response.deletedAt)}`
          : ''}
      </Typography>
    </Box>
  );
};

/**
 * Structured audit timeline for one assigned survey: assignment header, phases
 * with every recorded response (including jumped and soft-deleted rows), the
 * reopen log, and action-survey dispositions with tombstones.
 *
 * @param history - Assigned-survey history payload from the API
 * @returns Read-only timeline
 */
const AssignedSurveyHistoryTimeline = ({
  history,
}: AssignedSurveyHistoryTimelineProps) => {
  const phaseGroups = groupHistoryResponsesByPhase(history);

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <HistoryCard>
        <Typography component="div" variant="titleMedium">
          {history.surveyDisplayName || history.surveyName}
        </Typography>
        <Box display="flex" flexDirection="column" gap="8px">
          <LabeledValue
            label="Assigned"
            value={`${formatHistoryTimestamp(history.createdAt)}${
              history.isManuallyAssigned ? ' (manually assigned)' : ''
            }`}
          />
          <LabeledValue
            label="Completed"
            value={
              history.completedAt
                ? formatHistoryTimestamp(history.completedAt)
                : 'Not completed'
            }
          />
          {history.assignmentReason && (
            <LabeledValue
              label="Assignment reason"
              value={history.assignmentReason}
            />
          )}
        </Box>
      </HistoryCard>

      <Box display="flex" flexDirection="column" gap="12px">
        <SectionHeading iconName="ListMagnifyingGlass" title="Phases" />
        {phaseGroups.length === 0 && (
          <Typography component="div" variant="bodyMedium">
            No phases recorded yet.
          </Typography>
        )}
        {phaseGroups.map((group, index) => (
          <HistoryCard key={group.phase ? group.phase.id : 'orphaned'}>
            <FlexContainer
              direction="row"
              alignItems="center"
              gap={1}
              flexWrap
              height="auto"
            >
              <Typography component="div" variant="titleMedium">
                {group.phase
                  ? group.phase.checkpointTitle || `Phase ${index + 1}`
                  : 'Responses without a phase'}
              </Typography>
              {group.phase?.isCompletionCheckpoint && (
                <TaggableChip
                  label="Completion checkpoint"
                  color="success"
                  size="small"
                  isTag
                />
              )}
              {group.phase?.deletedAt && (
                <TaggableChip
                  label="Deleted"
                  color="error"
                  size="small"
                  isTag
                />
              )}
            </FlexContainer>
            {group.phase && (
              <Typography
                component="div"
                variant="labelMedium"
                color="text.secondary"
              >
                {`Started ${formatHistoryTimestamp(group.phase.createdAt)} · next due ${formatHistoryTimestamp(
                  group.phase.nextPhaseDueAt
                )} · completed ${formatHistoryTimestamp(group.phase.completedAt)}`}
              </Typography>
            )}
            {group.responses.length === 0 && (
              <Typography component="div" variant="bodyMedium">
                No responses recorded in this phase.
              </Typography>
            )}
            {group.responses.map(response => (
              <ResponseRow key={response.id} response={response} />
            ))}
          </HistoryCard>
        ))}
      </Box>

      <Box display="flex" flexDirection="column" gap="12px">
        <SectionHeading iconName="ArrowsCounterClockwise" title="Reopens" />
        {history.reopens.length === 0 && (
          <Typography component="div" variant="bodyMedium">
            This survey has never been reopened.
          </Typography>
        )}
        {history.reopens.map(reopen => (
          <HistoryCard key={reopen.id}>
            <Typography component="div" variant="bodyMedium">
              {`Reopened ${formatHistoryTimestamp(reopen.reopenedAt)}`}
            </Typography>
            <Typography
              component="div"
              variant="labelMedium"
              color="text.secondary"
            >
              {`Cleared a completion from ${formatHistoryTimestamp(
                reopen.wasCompletedAt
              )}`}
            </Typography>
          </HistoryCard>
        ))}
      </Box>

      <Box display="flex" flexDirection="column" gap="12px">
        <SectionHeading iconName="ClipboardText" title="Dispositions" />
        {!history.isActionSurvey && (
          <Typography component="div" variant="bodyMedium">
            This is not an action survey, so no dispositions are expected.
          </Typography>
        )}
        {history.isActionSurvey && history.dispositions.length === 0 && (
          <Typography component="div" variant="bodyMedium">
            No disposition has been recorded for this action survey.
          </Typography>
        )}
        {history.dispositions.map(disposition => (
          <HistoryCard key={disposition.id}>
            <FlexContainer
              direction="row"
              alignItems="center"
              gap={1}
              flexWrap
              height="auto"
            >
              <Typography component="div" variant="titleMedium">
                {disposition.disposition || 'Unlabeled disposition'}
              </Typography>
              {disposition.deletedAt && (
                <TaggableChip
                  label="Superseded"
                  color="warning"
                  size="small"
                  isTag
                />
              )}
            </FlexContainer>
            <Typography
              component="div"
              variant="labelMedium"
              color="text.secondary"
            >
              {`Offered ${formatHistoryTimestamp(disposition.offeredDate)} · action taken ${formatHistoryTimestamp(
                disposition.actionTakenDate
              )} · verified ${formatHistoryTimestamp(disposition.verificationDate)}`}
            </Typography>
          </HistoryCard>
        ))}
      </Box>
    </Box>
  );
};

export default AssignedSurveyHistoryTimeline;
